import { useCallback, useSyncExternalStore } from 'react';
import { getDatasetEnvironmentRegistry } from '~/engine/scene/datasetEnvironmentRegistryInstance';
import type { Environment } from '~/engine/data/Environment';
import type { DatasetEnvironmentRegistry } from '~/engine/scene/DatasetEnvironmentRegistry';
import { sameElements } from '~/utils/array';

type ResidentReader = Pick<
    DatasetEnvironmentRegistry,
    'environmentFor' | 'listEntries' | 'subscribeLifecycle'
>;

/** The last list read per registry, held while its membership is unchanged. */
const residentSnapshots = new WeakMap<ResidentReader, readonly Environment[]>();

/**
 * Every resident Environment of the registry, in registry order. The same
 * list comes back while the membership is unchanged, which is what lets a
 * store subscription settle and an effect key on the list.
 */
function readResidentEnvironments(
    registry: ResidentReader
): readonly Environment[] {
    const residents: Environment[] = [];
    for (const entry of registry.listEntries()) {
        const environment = registry.environmentFor(entry.seriesUid);
        if (environment) {
            residents.push(environment);
        }
    }
    const previous = residentSnapshots.get(registry);
    if (previous && sameElements(previous, residents)) {
        return previous;
    }
    residentSnapshots.set(registry, residents);
    return residents;
}

/**
 * Every resident Environment of the registry, in registry order. The list
 * keeps its identity while its membership is unchanged, and refreshes on
 * the registry lifecycle, since Environments are minted and disposed outside
 * Recoil.
 */
export function useResidentEnvironments(): readonly Environment[] {
    const registry = getDatasetEnvironmentRegistry();
    const subscribe = useCallback(
        (listener: () => void) => registry.subscribeLifecycle(listener),
        [registry]
    );
    const read = useCallback(
        () => readResidentEnvironments(registry),
        [registry]
    );
    return useSyncExternalStore(subscribe, read);
}
